import { type ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  EditButton,
  ReferenceField,
  Show,
  TextField,
  useGetList,
  useRecordContext,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess, type AccessibleRegion } from '../auth/permissions';
import type { Market } from '../dataProvider';
import { ActiveChip } from './CharityList';

/**
 * Charity detail (Show). Read-only view of the charity plus the soft-disable
 * state. Shows how many donations reference it — the concrete reason a charity is
 * DEACTIVATED, never deleted (D-charity-softdelete): destroying it would orphan
 * real donation history.
 *
 * The Edit affordance is gated `charities:write` (a viewer never sees it; the
 * edit route is guarded too). Region-scoped: a charity outside the viewer's
 * market is not viewable, even by direct URL.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

// Cheap read-only reference count — donations pointing at this charity.
const DonationCount = ({ charityId }: { charityId: number }) => {
  const { total } = useGetList('cashouts', {
    pagination: { page: 1, perPage: 1 },
    filter: { charityId },
  });
  return <Field label="Donations referencing this charity">{total ?? 0}</Field>;
};

const CharityShowLayout = () => {
  const record = useRecordContext();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  if (!record) return null;

  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  const canEdit = hasAccess(
    { role, accessibleRegions },
    { capability: 'charities:write' },
  );
  const logoUrl = record.logoUrl as string | null;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5" component="h1">
                {record.name as string}
              </Typography>
              <ReferenceField
                source="charityTypeId"
                reference="charityTypes"
                link={false}
              >
                <TextField source="label" />
              </ReferenceField>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
              <ActiveChip />
              {canEdit && <EditButton label="Edit" />}
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Field label="Description">{record.description as string}</Field>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="Market">
              {(record.market as string).toUpperCase()}
            </Field>
            <DonationCount charityId={record.id as number} />
            <Field label="Logo URL">
              {logoUrl ? (
                <Link
                  href={logoUrl}
                  target="_blank"
                  rel="noopener"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {logoUrl}
                </Link>
              ) : (
                '—'
              )}
            </Field>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const CharityShowView = () => (
  <Show title="Charity" actions={false} resource="charities">
    <CharityShowLayout />
  </Show>
);

/** Gated entry point registered as the `charities` Resource's show. */
export const CharityShow = () => (
  <RequireAccess capability="charities:read">
    <CharityShowView />
  </RequireAccess>
);
